import * as firebase from "firebase/app";
import "firebase/firestore";
import "@/plugins/vee-validate";

import moment from "moment";
import TimePicker from "@/components/pickers/TimePicker.vue";
import Comments from "@/components/comments/Comments.vue";
import Loader from "@/components/loader/Loader.vue";
import RejectRequestDialog from "@/views/services/view-service/reject-request-dialog/RejectRequestDialog.vue";
import ManipulateCalendarEventDialog from "@/views/services/view-service/manipulate-calendar-event-dialog/ManipulateCalendarEventDialog.vue";
import RequestPOAPriceDlg from "@/views/concierge/bookings-and-orders/request-details/request-POA-price-dlg/RequestPOAPriceDlg.vue";

import { ValidationObserver, ValidationProvider } from "vee-validate";
import { ServiceStatus } from "@/enums/ServiceStatus";
import { ServiceTypeEnum } from "@/enums/ServiceTypeEnum";
import { ValidationRules } from "@/utils/validation-rules";
import { getTooltipTextKey } from "./tooltips";
import { PremiumServiceEventTypes } from "@/enums/PremiumServiceEventTypes";
import { getFormattedDateTimeString } from "@/utils/dateTimeFormat";
import { getRegionName } from "@/utils/functionsRegion";
import { DefaultEmailTemplateType } from "@/enums/DefaultEmailTemplateType";
import { delay } from "@/utils/wait-resized-image";

export default {
  name: "RequestDetails",
  components: {
    Loader,
    Comments,
    ValidationObserver,
    ValidationProvider,
    ManipulateCalendarEventDialog,
    TimePicker,
    RejectRequestDialog,
    RequestPOAPriceDlg
  },
  data() {
    return {
      onServiceRequestsUnsubscribe: null,
      staffUnreadCommentsCount: 0,
      showCommentsButton: false,
      isChatActive: false,
      loading: true,
      reminderEmailSending: false,
      approvalIndex: -1,
      event: {
        description: "",
        start: new Date(),
        end: new Date()
      },
      commentsData: { serviceRequestId: this.$route.params.id, serviceItemType: null, serviceName: null, bookByFamilyId: null, bookByResidentId: null },
      headers: [],
      manualyApproveDialog: false,
      manualyApproveServiceRequestLoading: false,
      selectedItem: null,
      error: {
        request: false,
        service: false
      },
      reminderEmailTemplate: null
    }
  },
  computed: {
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    request() {
      return this.$store.state.additionalServicesModule.serviceRequest;
    },
    approvals() {
      return this.$store.state.additionalServicesModule.serviceRequestApprovals;
    },
    additionalService() {
      return this.$store.state.additionalServicesModule.additionalService;
    },
    isServiceHasPhoto() {
      return this.additionalService?.imageUrl && !this.additionalService?.imageUrl.includes("no-image");
    },
    resident() {
      return this.$store.state.residentModule.currentResident;
    },
    user() {
      return this.$store.getters.user;
    },
    isVisibleRejectButton() {
      return [this.serviceStatus.AwaitingApproval, this.serviceStatus.Approved].includes(this.request.status);
    },
    serviceStatus() {
      return ServiceStatus;
    },
    serviceTypeEnum() {
      return ServiceTypeEnum;
    },
    bookedBy() {
      if (this.request.bookByFamilyName) {
        return this.request.bookByFamilyName;
      }

      if (this.request.bookByStaffName) {
        return this.request.bookByStaffName;
      }

      return this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Resident'];
    },
    cost() {
      if (this.request.serviceType === ServiceTypeEnum.DailyCharge && this.request.paymentCost !== "POA") {
        return `${this.facilityCurrency} ${this.request.paymentCost} / ${this.request.period}`;
      } else if (this.request.paymentCost === "POA") {
        return this.request.paymentCost;
      } else {
        const paymentCost = this.request.paymentCost;
        return Number(paymentCost)
          ? `${this.facilityCurrency} ${paymentCost}`
          : paymentCost;
      }
    },
    rejectComment() {
      return this.request.rejectComment
    },
    isPoa() {
      return this.cost === "POA";
    },
    rules() {
      return {
        start: [ValidationRules.required],
        end: [
          ValidationRules.required,
          v =>
            moment(v).isAfter(moment(this.event.start)) ||
            this.$i18n.messages[this.$i18n.locale]['ViewService.EventEndDateCannotBeBeforeStartDate']
        ]
      };
    },
    tooltipText() {
      const key = getTooltipTextKey(this.request.status)
      return this.$i18n.t(key)
    },
    conciergeUpcomingEvents() {
      return (this.request.conciergeUpcomingEvents || []);
    },
    futureEvents() {
      return (
        this.additionalService?.events.filter(event => {
          const start = event.start.seconds * 1000;
          return moment(start).isSameOrAfter(moment(), "day");
        }) || []
      );
    },
    events() {
      const allEvents = [...this.conciergeUpcomingEvents, ...this.futureEvents];

      const uniqueEvents = allEvents.filter((event, index, self) =>
        index === self.findIndex((e) => e.id === event.id)
      );

      return uniqueEvents;
    },
    facilityCurrency() {
      return this.$store.getters.facilityCurrency;
    },
    PremiumServiceEventTypes() {
      return PremiumServiceEventTypes;
    },
    serviceType() {
      const type = this.additionalService.type;

      if ([this.PremiumServiceEventTypes.Request, this.PremiumServiceEventTypes.AppAccess, this.PremiumServiceEventTypes.ByAppointment].includes(type)) {
        return this.PremiumServiceEventTypes.Request;
      } else if ([this.PremiumServiceEventTypes.EventBased, this.PremiumServiceEventTypes.ConciergeEvent, this.PremiumServiceEventTypes.Event].includes(type)) {
        return this.PremiumServiceEventTypes.Event;
      } else if ([this.PremiumServiceEventTypes.Gift, this.PremiumServiceEventTypes.Product].includes(type)) {
        return this.PremiumServiceEventTypes.Product;
      } else {
        return type;
      }
    },
  },
  async created() {
    Promise.all([
      await this.$store.dispatch("getAdditionalServiceRequest", this.$route.params.id),
      this.checkRequest(),
      await this.$store.dispatch("getAdditionalService", this.request.serviceId),
      this.checkAdditionalService(),
      this.$store.dispatch("getAdditionalServiceApprovals", this.$route.params.id),
      this.initChat(),
      this.setHeadersForTable()
    ]).then(() => {
      this.commentsData.bookByFamilyId = this.request.bookByFamilyId
      this.commentsData.serviceItemType = this.request.serviceItemType 
      this.commentsData.serviceName = this.request.serviceName
      this.commentsData.residentId = this.request.residentId
      this.showCommentsButton = true
      this.loading = false;
    })
  },
  beforeDestroy() {
    this.unsubscribeFromserviceRequest();
  },
  methods: {
    setHeadersForTable() {
      this.headers = [
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Name'],
          align: "start",
          sortable: false,
          value: "familyHubMemberName"
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['ViewService.ApprovalStatus'],
          align: "start",
          sortable: false,
          value: "approved"
        },
        {
          text: "",
          align: "end",
          sortable: false,
          value: "reminderEmail"
        },
        {
          text: "",
          align: "start",
          sortable: false,
          value: "manualyApprove"
        }
      ]
    },
    onCommentsClose() {
      this.isChatActive = false
    },
    toggleChat() {
      this.isChatActive = !this.isChatActive;
    },
    openRejectRequest() {
      this.$refs.RejectRequestDialog.open(this.request, this.approvals);
    },
    completeServiceRequest() {
      this.request.serviceItemType = this.additionalService.type
      this.$store.dispatch("completeServiceRequest", {
        svs: this.request
      });
      this.updateAdditionalService();
    },
    backToList() {
      this.$router.push(`/bookings-and-orders`)
    },
    checkRequest() {
      if (this.request.isServiceDeleted) {
        this.error.request = true;
        this.loading = false;
        return false;
      }
    },
    checkAdditionalService() {
      if (!this.additionalService) {
        this.error.service = true;
        this.loading = false;
        return false;
      }
    },
    async initChat() {
      const query = firebase.firestore().collection("serviceRequests").doc(this.$route.params.id)
      this.onServiceRequestsUnsubscribe = await query
        .onSnapshot((snapshot) => {
          this.staffUnreadCommentsCount = snapshot.data()?.staffUnreadCommentsCount
        })
    },
    unsubscribeFromserviceRequest() {
      console.log('unsubscribeFromserviceRequest')
      if (this.onServiceRequestsUnsubscribe) {
        this.onServiceRequestsUnsubscribe()
      }
    },
    updateAdditionalService() {
      const additionalService = this.additionalService;

      additionalService.membersList = additionalService?.membersList?.filter(
        member => {
          if (member.serviceRequestId !== this.request.id) {
            return member;
          }
        }
      ) || []

      additionalService.membersIds = additionalService?.membersIds?.filter(
        memberId => {
          if (
            !this.additionalService?.membersList.find(
              member => member === memberId
            )
          ) {
            return memberId;
          }
        }
      ) || []

      this.$store.dispatch("updateAdditionalService", {
        id: additionalService.id,
        membersList: additionalService.membersList,
        membersIds: additionalService.membersIds
      });
    },
    selectItem(item) {
      this.manualyApproveDialog = true;
      this.selectedItem = item;
    },
    async manualyApproveServiceRequest() {
      if (this.manualyApproveServiceRequestLoading === true) return

      this.manualyApproveServiceRequestLoading = true
      this.$store.dispatch("updateAdditionalServiceRequestApprove", this.selectedItem).then(() => {
        if (this.approvals?.length > 1) {
          //TODO: multiple approvements in the same time cause issue in fb trigger,
          //so we need to add this delay or rework code to update additionalServiceRequestApprovals doc and exec code from the trigger "onUpdate" atomically in case with many approvals in the same time (RES-3339).
          delay(2000).then(() => {
            this.manualyApproveDialog = false;
            this.manualyApproveServiceRequestLoading = false
          })
        } else {
          this.manualyApproveDialog = false;
          this.manualyApproveServiceRequestLoading = false
        }
      })
    },
    formattedDateTimeString(date: Date, addYears: boolean, addTime: boolean) {
      if (!this.facility) return ''

      return getFormattedDateTimeString(date, this.facility.dateFormat, addTime ? this.facility.timeFormat : null, addYears);
    },
    async getEmailTemplateId() {
      await firebase
        .firestore()
        .collection('emailTemplates')
        .where('facilityId', '==', this.$store.state.authModule.currentFacility)
        .where('type', '==', DefaultEmailTemplateType.FamilyReminderEmail)
        .get()
        .then((querySnapshot) => {

          const emailTemplates: any[] = querySnapshot.docs.map((x) => ({ id: x.id, ...x.data() }))

          this.reminderEmailTemplate = emailTemplates.find(x => x.isEnabled)

          if (!this.reminderEmailTemplate) {
            this.reminderEmailTemplate = emailTemplates[0]
          }
        });
    },
    async reminderEmail(additionalServiceRequestApproval) {
      this.reminderEmailSending = true

      await this.getEmailTemplateId();

      await firebase.app().functions(getRegionName())
        .httpsCallable("emails-sendEmailTemplate")({
          emailTemplateId: this.reminderEmailTemplate.id,
          toFamilyMembersIds: [additionalServiceRequestApproval.familyHubMemberId],
          templateData: null,
          mailingListIds: null,
          isSendToFamilyMemberDevice: false,
          isSendDisabledEmailTemplate: true,
          isAutomationTrigger: true,
          sendOptions: {
            additionalData: {
              serviceRequestApprovalId: additionalServiceRequestApproval.id,
              residentName: additionalServiceRequestApproval.residentName,
              serviceName: additionalServiceRequestApproval.serviceName
            }
          }
        })
        .then((snap: any) => {
          this.$toast.open({
            message: this.$i18n.messages[this.$i18n.locale]['ViewService.MessageSentSuccessfully'],
            type: "success"
          });
        }).catch((error: any) => {
          this.$toast.open({
            message: this.$i18n.messages[this.$i18n.locale]['ViewService.CannotSendMessage'],
            type: "error"
          });
          console.error(error);
        }).finally(() => {
          this.reminderEmailSending = false
        })
    },
    appendHours(hours) {
      return hours > 1 ? 'hours' : 'hour';
    },
    openRequestPOAPriceDlg() {
      this.$refs.requestPOAPriceDlg.open();
    }
  },
  watch: {
    request(request) {
      this.$store.dispatch("getResident", request.residentId);
    },
    approvals(value) {
      this.approvals = value;
    }
  }
}
